import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  Button,
  Stack,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  LinkOff as LinkOffIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";

import WidgetOverlayRow from "./WidgetOverlayRow";
import SelectionHandles from "./SelectionHandles";
import { pdfRectToCanvasRect } from "../../../../../../../utils/coordinateUtils";

const cleanPdfTextForLabel = (text = "") =>
  String(text || "")
    .replace(/[-_\\.:*]{3,}/g, " ")
    .replace(/^[-_\\.:*\s]+/, "")
    .replace(/[-_\\.:*\s]+$/, "")
    .replace(/\s+/g, " ")
    .trim();

const SMALL_TITLE_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "in",
  "nor",
  "of",
  "on",
  "or",
  "the",
  "to",
  "up",
  "via",
  "with",
]);

const capitalizeWord = (word = "") =>
  String(word || "")
    .toLowerCase()
    .replace(/^([a-z])/, (match) => match.toUpperCase())
    .replace(/(['’])S\b/g, "$1s");

const toTitleCase = (text = "") =>
  String(text || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index, words) => {
      const normalizedWord = word.replace(/^[^a-z]+|[^a-z]+$/gi, "");
      const isSmallWord = SMALL_TITLE_WORDS.has(normalizedWord);
      const isEdgeWord = index === 0 || index === words.length - 1;

      return isSmallWord && !isEdgeWord ? word : capitalizeWord(word);
    })
    .join(" ");

const FormFieldOverlayLayer = ({
  currentProcessingFile: committedProcessingFile,
  widgetMovePreviewRef,
  pageNumber,
  scale,
  selectToolActive,
  typingToolActive,
  highlightFields,
  textOverlayDisplayMode,
  focusedField,
  setFocusedField,
  currCursor,
  unassignedFieldNames,
  fixedInputs,
  inputRef,
  childInputRef,
  pdfContainerRef,
  setCurrentProcessingFile,
  setStartPoint,
  setRect,
  setIsDrawing,
  setCurrCursor,
  setFreeSelectStartPoint,
  setMoving,
  setIsSelecting,
  setInitialMousePosition,
  setInitialInputSize,
  setInputPosition,
  setSelectionRect,
  setResizing,
  setResizeDirection,
  handleMouseDown,
  handleFieldChange,
  undoRedoStats,
  activeEditorTool,
  isPreview = false,
  baseViewPort,
  fieldList,
  onNavigateToTaggedField,
  formFields,
  onPreviewFieldClick,
  previewHoveredFieldNames = [],
  onFavouriteDrop,
  favouriteDropDisabled = false,
}) => {
  const [movePreviewFile, setMovePreviewFile] = useState(null);
  React.useImperativeHandle(
    widgetMovePreviewRef,
    () => ({
      setFile: setMovePreviewFile,
    }),
    [],
  );
  const currentProcessingFile = movePreviewFile || committedProcessingFile;

  const pageHeight =
    currentProcessingFile?.document?.tempFields?.currentDocument
      ?.actualHeight || 792;
  const viewport =
    currentProcessingFile?.document?.tempFields?.currentDocument?.viewport ||
    null;
  const pageXMin =
    currentProcessingFile?.document?.tempFields?.currentDocument?.pageXMin || 0;
  const pageYMin =
    currentProcessingFile?.document?.tempFields?.currentDocument?.pageYMin || 0;
  const scaleFactor =
    currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor ||
    1;

  const [openEditBlockDialog, setOpenEditBlockDialog] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [deletedFields, setDeletedFields] = useState([]);

  const handleTaggedFieldNavigation = (event, field) => {
    if (!event.altKey || !field?.field_id) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    onNavigateToTaggedField?.(field);
    return true;
  };

  const buildLabelFromSelectedPdfText = React.useCallback((tempFields) => {
    const primaryText = cleanPdfTextForLabel(
      tempFields?.selectedPdfTextItem?.text || "",
    );
    const extraTexts = (tempFields?.selectedExtraPdfTextItems || [])
      .map((item) => cleanPdfTextForLabel(item?.text || ""))
      .filter(Boolean);

    return toTitleCase(
      [primaryText, ...extraTexts]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim(),
    );
  }, []);

  const sameRect = (a, b) =>
    !!a && !!b && a.X === b.X && a.Y === b.Y && a.W === b.W && a.H === b.H;
  const createFieldMatcher = (target, allFields) => {
    if (!target) return () => false;

    if (target.field_instance_id) {
      return (f) =>
        !!f?.field_instance_id &&
        f.field_instance_id === target.field_instance_id;
    }

    if (target.name) {
      const count = (allFields || []).filter(
        (f) => f?.name === target.name,
      ).length;
      if (count === 1) return (f) => f?.name === target.name;
    }

    if (target.field_id && target.page != null && target.order != null) {
      const matches = (allFields || []).filter(
        (f) =>
          f?.field_id === target.field_id &&
          f?.page === target.page &&
          f?.order === target.order,
      );
      if (matches.length === 1) {
        return (f) =>
          f?.field_id === target.field_id &&
          f?.page === target.page &&
          f?.order === target.order;
      }
    }

    if (target.field_id && target.rect) {
      const matches = (allFields || []).filter(
        (f) =>
          f?.field_id === target.field_id && sameRect(f?.rect, target.rect),
      );
      if (matches.length === 1) {
        return (f) =>
          f?.field_id === target.field_id && sameRect(f?.rect, target.rect);
      }
    }

    if (target.field_id) {
      const matches = (allFields || []).filter(
        (f) => f?.field_id === target.field_id,
      );
      if (matches.length === 1) {
        return (f) => f?.field_id === target.field_id;
      }
    }
    return () => false;
  };

  const assignLabelFromSelectedPdfText = React.useCallback(
    (prevFile, field) => {
      const tempFields = prevFile?.document?.tempFields || {};
      const nextLabel = buildLabelFromSelectedPdfText(tempFields);

      if (!nextLabel || !field) {
        return prevFile;
      }

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...tempFields,
            formFields: (() => {
              const allFields = tempFields.formFields || [];
              const isMatchingField = createFieldMatcher(field, allFields);
              return allFields.map((existingField) =>
                isMatchingField(existingField)
                  ? {
                      ...existingField,
                      field_label: nextLabel,
                      label: nextLabel,
                    }
                  : existingField,
              );
            })(),
            selectedFormField: null,
            selectedExtraFields: [],
            selectedPdfTextItem: null,
            selectedExtraPdfTextItems: [],
          },
        },
      };
    },
    [buildLabelFromSelectedPdfText],
  );

  const handleCloseEditBlockDialog = () => {
    setOpenEditBlockDialog(false);
    setSelectedBlock(null);
  };

  const pickRectComponent = (rect = {}, upper, lower, fallback = 0) => {
    const value = rect?.[upper] ?? rect?.[lower] ?? fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number(fallback) || 0;
  };

  const getBoundingBox = useCallback(
    (fields, padding = 8) => {
      const currentDocument =
        currentProcessingFile?.document?.tempFields?.currentDocument || {};

      const minY = Math.min(...fields.map((f) => f.y));
      const viewport = currentDocument?.viewport || null;

      const topField = fields?.length
        ? fields.reduce((maxItem, item) =>
            item.y < maxItem.y ? item : maxItem,
          )
        : {};

      const y = pickRectComponent(topField, "Y", "y", 0);

      const maxY = Math.max(...fields.map((f) => f.y + f.height));
      return {
        x: 20,
        y: y - 3,
        width: viewport?.width - 50,
        height: (maxY - minY + padding * 0.5) * scaleFactor,
      };
    },
    [currentProcessingFile?.document?.tempFields?.currentDocument, scaleFactor],
  );

  const handleEditConfirm = (isAll) => {
    const tempFields = currentProcessingFile?.document?.tempFields;

    const allSelectedField =
      isAll ||
      tempFields?.formFields?.filter(
        (f) => f?.block_id === selectedBlock && !f?.name?.startsWith("block__"),
      )?.length === deletedFields?.length;

    const updatedFormFields = tempFields?.formFields?.map((f) => {
      if (
        allSelectedField &&
        f?.block_id === selectedBlock &&
        !f?.name?.startsWith("block__")
      ) {
        return {
          ...f,
          block_id: "",
          block_label: "",
        };
      } else if (deletedFields?.includes(f?.name)) {
        return {
          ...f,
          block_id: "",
          block_label: "",
        };
      } else {
        return f;
      }
    });

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: allSelectedField
            ? updatedFormFields?.filter((f) => f?.block_id !== selectedBlock)
            : updatedFormFields?.map((f) => {
                if (
                  f?.block_id === selectedBlock &&
                  f?.name?.startsWith("block__")
                ) {
                  const bb = getBoundingBox(
                    updatedFormFields?.filter(
                      (f) =>
                        f?.block_id === selectedBlock &&
                        !f?.name?.startsWith("block__"),
                    ),
                    8,
                  );

                  return {
                    ...f,
                    y: bb?.y ?? f?.y,
                    height: bb?.height ?? f?.height,
                  };
                } else {
                  return f;
                }
              }),
        },
      },
    }));

    setOpenEditBlockDialog(false);
    setSelectedBlock(null);
    setDeletedFields([]);
  };

  const rowEventContextRef = React.useRef({
    currentProcessingFile,
    setCurrentProcessingFile,
    onPreviewFieldClick,
    onFavouriteDrop,
    handleTaggedFieldNavigation,
    handleMouseDown,
    setStartPoint,
    setRect,
    setIsDrawing,
    setCurrCursor,
    setFreeSelectStartPoint,
    setMoving,
    setIsSelecting,
    setInitialMousePosition,
    setInitialInputSize,
    setInputPosition,
    setSelectionRect,
    setResizing,
    setResizeDirection,
    setFocusedField,
    handleFieldChange,
    assignLabelFromSelectedPdfText,
  });
  React.useLayoutEffect(() => {
    rowEventContextRef.current = {
      currentProcessingFile,
      setCurrentProcessingFile,
      onPreviewFieldClick,
      onFavouriteDrop,
      handleTaggedFieldNavigation,
      handleMouseDown,
      setStartPoint,
      setRect,
      setIsDrawing,
      setCurrCursor,
      setFreeSelectStartPoint,
      setMoving,
      setIsSelecting,
      setInitialMousePosition,
      setInitialInputSize,
      setInputPosition,
      setSelectionRect,
      setResizing,
      setResizeDirection,
      setFocusedField,
      handleFieldChange,
      assignLabelFromSelectedPdfText,
    };
  });
  const updateFileForRow = useCallback(
    (...args) => rowEventContextRef.current.setCurrentProcessingFile(...args),
    [],
  );
  const handleFavouriteDrop = useCallback(
    (...args) => rowEventContextRef.current.onFavouriteDrop?.(...args),
    [],
  );

  const fieldListById = useMemo(() => {
    const result = new Map();
    for (const field of fieldList || []) {
      if (!result.has(field?.field_id)) result.set(field?.field_id, field);
    }
    return result;
  }, [fieldList]);
  const formFieldsById = useMemo(() => {
    const result = new Map();
    for (const field of formFields || []) {
      if (!result.has(field?.field_id)) result.set(field?.field_id, field);
    }
    return result;
  }, [formFields]);
  const unassignedNames = useMemo(
    () =>
      new Set(Array.isArray(unassignedFieldNames) ? unassignedFieldNames : []),
    [unassignedFieldNames],
  );
  const hoveredNames = useMemo(
    () => new Set(previewHoveredFieldNames),
    [previewHoveredFieldNames],
  );
  const rectCache = useMemo(
    () => new WeakMap(),
    [isPreview, baseViewPort, viewport, pageHeight, scale, pageXMin, pageYMin],
  );
  const getCanvasRect = (field) => {
    let rect = rectCache.get(field);
    if (!rect) {
      rect = pdfRectToCanvasRect(
        field,
        isPreview ? baseViewPort : viewport || pageHeight,
        scale,
        pageXMin,
        pageYMin,
      );
      rectCache.set(field, rect);
    }
    return rect;
  };

  const tempFields = currentProcessingFile?.document?.tempFields || {};
  const selectedFieldNames = new Set(
    [tempFields.selectedFormField, ...(tempFields.selectedExtraFields || [])]
      .filter(Boolean)
      .map((field) => field.name),
  );
  const selectedFieldsOnPage = (tempFields.formFields || []).filter((field) => {
    if (!selectedFieldNames.has(field.name) || field?.hidden) return false;

    const rawPage = field?.page_num ?? field?.page;
    const parsedPage = Number(rawPage);
    return Number.isFinite(parsedPage) ? parsedPage === pageNumber : true;
  });
  const selectedCanvasRects = selectedFieldsOnPage.map(getCanvasRect);
  const groupSelectionRect =
    selectedCanvasRects.length > 1
      ? {
          left: Math.min(...selectedCanvasRects.map((rect) => rect.left)),
          top: Math.min(...selectedCanvasRects.map((rect) => rect.top)),
          width:
            Math.max(
              ...selectedCanvasRects.map((rect) => rect.left + rect.width),
            ) - Math.min(...selectedCanvasRects.map((rect) => rect.left)),
          height:
            Math.max(
              ...selectedCanvasRects.map((rect) => rect.top + rect.height),
            ) - Math.min(...selectedCanvasRects.map((rect) => rect.top)),
        }
      : null;
  const groupResizeAnchorField =
    selectedFieldsOnPage.find(
      (field) => field.name === tempFields.selectedFormField?.name,
    ) || selectedFieldsOnPage[0];

  return (
    <>
      {groupSelectionRect &&
        groupResizeAnchorField &&
        selectToolActive &&
        !isPreview && (
          <Box
            sx={{
              position: "absolute",
              left: `${groupSelectionRect.left}px`,
              top: `${groupSelectionRect.top}px`,
              width: `${groupSelectionRect.width}px`,
              height: `${groupSelectionRect.height}px`,
              outline: "2px dotted blue",
              outlineOffset: "0px",
              boxSizing: "border-box",
              backgroundColor: "transparent",
              pointerEvents: "none",
              zIndex: 101,
              "& > *": { pointerEvents: "auto" },
            }}
          >
            <SelectionHandles
              onHandleMouseDown={(e, pos) => {
                e.stopPropagation();
                handleMouseDown(
                  e,
                  currentProcessingFile,
                  setCurrentProcessingFile,
                  groupResizeAnchorField,
                  scale,
                  pos.direction,
                  pos.cursor,
                  pdfContainerRef,
                  setStartPoint,
                  setRect,
                  setIsDrawing,
                  setCurrCursor,
                  setFreeSelectStartPoint,
                  setMoving,
                  setIsSelecting,
                  setInitialMousePosition,
                  setInitialInputSize,
                  setInputPosition,
                  setSelectionRect,
                  setResizing,
                  setResizeDirection,
                  pageNumber,
                );
              }}
            />
          </Box>
        )}

      {currentProcessingFile?.document?.tempFields?.formFields
        .filter((field) => {
          if (field?.hidden) {
            return false;
          }

          const rawPage = field?.page_num ?? field?.page;
          const parsedPage = Number(rawPage);
          return Number.isFinite(parsedPage) ? parsedPage === pageNumber : true;
        })
        .map((field, index) => {
          const mainRect = getCanvasRect(field);
          const checkboxRect = mainRect;

          const isPrimarySelected =
            currentProcessingFile?.document?.tempFields?.selectedFormField
              ?.name === field?.name;

          const isExtraSelected =
            !isPrimarySelected && selectedFieldNames.has(field.name);
          const isUnassigned =
            !field?.block_id ||
            !field?.field_id ||
            unassignedNames.has(field?.name);
          const definition = fieldListById.get(field?.field_id);
          const parentDefinition =
            definition?.field_type === "option" && definition?.parent_field_id
              ? fieldListById.get(definition.parent_field_id)
              : undefined;
          const markCircle =
            definition?.mark_circle ?? parentDefinition?.mark_circle;
          const shouldMarkCircle =
            markCircle && isPreview && field?.value === true;
          const isMultiline = definition?.field_type === "multiline";
          const isPreviewHovered = hoveredNames.has(field?.name);
          return (
            <WidgetOverlayRow
              key={field.name ?? index}
              field={field}
              index={index}
              eventContextRef={rowEventContextRef}
              mainRect={mainRect}
              checkboxRect={checkboxRect}
              isPrimarySelected={isPrimarySelected}
              isExtraSelected={isExtraSelected}
              groupSelectionRect={
                isPrimarySelected && Boolean(groupSelectionRect)
              }
              selectToolActive={selectToolActive}
              typingToolActive={typingToolActive}
              highlightFields={highlightFields}
              textOverlayDisplayMode={textOverlayDisplayMode}
              focusedField={focusedField === field.name ? focusedField : null}
              currCursor={currCursor}
              isUnassigned={isUnassigned}
              fixedInputs={fixedInputs}
              activeEditorTool={activeEditorTool}
              setCurrentProcessingFile={updateFileForRow}
              isPreview={isPreview}
              formField={formFieldsById.get(field?.field_id)}
              shouldMarkCircle={shouldMarkCircle}
              isMultiline={isMultiline}
              isPreviewHovered={isPreviewHovered}
              setOpenEditBlockDialog={setOpenEditBlockDialog}
              setSelectedBlock={setSelectedBlock}
              onFavouriteDrop={
                typeof onFavouriteDrop === "function"
                  ? handleFavouriteDrop
                  : onFavouriteDrop
              }
              favouriteDropDisabled={favouriteDropDisabled}
              inputRef={inputRef}
              childInputRef={childInputRef}
              pdfContainerRef={pdfContainerRef}
              scale={scale}
              pageNumber={pageNumber}
            />
          );
        })}

      <Dialog
        open={openEditBlockDialog}
        onClose={handleCloseEditBlockDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Assignment</DialogTitle>
        <DialogContent>
          <Stack gap={1}>
            {currentProcessingFile?.document?.tempFields?.formFields
              ?.filter(
                (f) =>
                  f?.block_id === selectedBlock &&
                  !f?.name?.startsWith("block__"),
              )
              ?.map((field) => {
                const isFieldDeleted = deletedFields?.includes(field?.name);

                return (
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    key={field?.name}
                  >
                    <Typography>{field?.field_label}</Typography>
                    <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (isFieldDeleted) {
                            setDeletedFields(
                              deletedFields?.filter((f) => f !== field?.name),
                            );
                          } else {
                            setDeletedFields((prev) => [...prev, field?.name]);
                          }
                        }}
                        sx={{
                          border: "1px solid #d0d7de",
                          borderRadius: 1.5,
                          backgroundColor: "transparent",
                          p: "4px !important",
                        }}
                      >
                        {isFieldDeleted ? (
                          <AttachFileIcon
                            sx={{
                              fontSize: "1rem",
                              transform: "rotate(45deg)",
                            }}
                          />
                        ) : (
                          <LinkOffIcon sx={{ fontSize: "1rem" }} />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button
            onClick={() => handleEditConfirm(true)}
            variant="contained"
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
          >
            Unassign All
          </Button>
          <Box display={"flex"} alignItems={"center"} gap={1}>
            <Button
              onClick={handleCloseEditBlockDialog}
              variant="text"
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleEditConfirm()}
              variant="contained"
              sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            >
              Update
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(FormFieldOverlayLayer);
